import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, List, ListItem, LinearProgress } from '@mui/material';
import { MathJax, MathJaxContext } from 'better-react-mathjax';
import { askGPT } from './OpenAIService';
import { appConfig } from '../Config';
import { loadUserProgress, updateQuizProgress } from './progressManager';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import mathJaxConfig from './mathJaxConfig';

const totalQuiz = appConfig.totalQuiz;

const Quiz = ({ competence, onQuizComplete, userId }) => {
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [loading, setLoading] = useState(true);
  const [answered, setAnswered] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [totalCorrectAnswers, setTotalCorrectAnswers] = useState(0);
  const [totalQuestionsAnswered, setTotalQuestionsAnswered] = useState(0);
  const [error, setError] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);

  // Fonction pour charger les questions depuis Firestore
  const loadQuizFromFirestore = async () => {
    if (!userId || !competence?.id) return;

    try {
      const quizRef = doc(db, "users", userId, "quizzes", competence.id);
      const quizSnap = await getDoc(quizRef);

      if (quizSnap.exists()) {
        const quizData = quizSnap.data();
        setQuizQuestions(quizData.questions || []);
        setCurrentQuestionIndex(quizData.currentQuestionIndex || 0);
        setQuizCompleted(!quizData.questions || quizData.questions.length === 0);
      } else {
        setQuizCompleted(false);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération du quiz :", err);
      setError("Erreur lors de la récupération du quiz. Veuillez réessayer.");
    } finally {
      setLoading(false); // Assurez-vous de désactiver le loading ici
    }
  };

  // Fonction pour initialiser le quiz
  useEffect(() => {
    const initializeQuiz = async () => {
      if (!userId) {
        setError('Erreur : Utilisateur non connecté.');
        setLoading(false);
        return;
      }

      if (!competence || !competence.id) {
        setError('Erreur : Veuillez sélectionner une compétence.');
        setLoading(false);
        return;
      }

      try {
        await loadProgressFromFirestore(); // Récupération de la progression
        await loadQuizFromFirestore(); // Chargement des questions du quiz depuis Firestore
      } catch (err) {
        console.error("Erreur d'initialisation du quiz:", err);
        setError("Erreur lors du chargement du quiz.");
        setLoading(false);
      }
    };

    initializeQuiz();
  }, [competence?.id, userId]);

  // Fonction pour récupérer la progression de l'utilisateur
  const loadProgressFromFirestore = async () => {
    if (!userId || !competence?.id) return;

    try {
      const userProgress = await loadUserProgress(userId);
      const progressData = userProgress[competence.id] || {
        totalCorrectAnswers: 0,
        totalQuestionsAnswered: 0,
        quizCompleted: false,
        currentQuestionIndex: 0,
      };

      setTotalCorrectAnswers(progressData.totalCorrectAnswers);
      setTotalQuestionsAnswered(progressData.totalQuestionsAnswered);
      setQuizCompleted(progressData.quizCompleted);
      setCurrentQuestionIndex(progressData.currentQuestionIndex);
    } catch (err) {
      console.error("Erreur lors du chargement de la progression :", err);
      setError("Erreur lors du chargement de la progression. Veuillez réessayer.");
      setLoading(false);
    }
  };

  // Fonction pour générer un quiz via GPT si aucune question n'est trouvée
  const generateQuiz = async () => {
    if (!competence?.title) {
      setError("Titre de la compétence non défini.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const prompt = `Génère un quiz de ${totalQuiz} questions pour la compétence : "${competence.title}". 
                     Format JSON : {"questions": [{"question": "", "options": [], "answer": "", "feedbackCorrect": "", "feedbackIncorrect": ""}]}
                     Utilise $...$ pour les formules en ligne et $$...$$ pour les formules en mode affiché.`;

      const response = await askGPT(prompt);

      const jsonStartIndex = response.indexOf("{");
      const jsonEndIndex = response.lastIndexOf("}") + 1;
      if (jsonStartIndex === -1 || jsonEndIndex === -1) {
        throw new Error("Le JSON généré est mal formé ou absent.");
      }

      let jsonString = response.slice(jsonStartIndex, jsonEndIndex);
      jsonString = jsonString.replace(/\\/g, "\\\\"); // Escape les caractères de barre oblique

      const parsedQuiz = JSON.parse(jsonString).questions;

      setQuizQuestions(parsedQuiz);
      setCurrentQuestionIndex(0);
      setAnswered(false);
      setSelectedAnswer('');
      setQuizCompleted(false);

      await saveQuizToFirestore(parsedQuiz, 0);
    } catch (err) {
      console.error("Erreur lors de la génération du quiz :", err);
      setError('Erreur lors de la génération du quiz. Veuillez réessayer.');
    } finally {
      setLoading(false); // Fin du chargement du quiz
    }
  };

  // Fonction pour sauvegarder le quiz dans Firestore
  const saveQuizToFirestore = async (questions, questionIndex) => {
    if (!userId || !competence?.id) return;

    try {
      const quizRef = doc(db, "users", userId, "quizzes", competence.id);
      await setDoc(quizRef, {
        questions,
        currentQuestionIndex: questionIndex
      }, { merge: true });
    } catch (err) {
      console.error("Erreur lors de la sauvegarde du quiz :", err);
      throw new Error("Erreur lors de la sauvegarde du quiz.");
    }
  };

  // Modification de la fonction renderWithMathJax
  const renderWithMathJax = useCallback((content) => {
    if (!content) return null;

    const preparedContent = content
      .replace(/\$\$/g, ' $$ ')
      .replace(/([^$])\$/g, '$1 $')
      .replace(/\$([^$])/g, '$ $1')
      .trim();

    return (
      <Box component="span" className="math-wrapper">
        <MathJax inline dynamic>
          {preparedContent}
        </MathJax>
      </Box>
    );
  }, []);

  // Fonction de sélection d'option
  const handleOptionSelect = (option) => {
    setSelectedAnswer(option);
  };

  // Fonction de soumission de la réponse
  const handleAnswerSubmit = async () => {
    if (!quizQuestions || quizQuestions.length === 0 || !selectedAnswer) return;

    const currentQuestion = quizQuestions[currentQuestionIndex];
    const isCorrect = selectedAnswer === currentQuestion.answer;

    setFeedback(isCorrect ? currentQuestion.feedbackCorrect : currentQuestion.feedbackIncorrect);
    setAnswered(true);

    const newTotalCorrectAnswers = isCorrect ? totalCorrectAnswers + 1 : totalCorrectAnswers;
    const newTotalQuestionsAnswered = totalQuestionsAnswered + 1;

    setTotalCorrectAnswers(newTotalCorrectAnswers);
    setTotalQuestionsAnswered(newTotalQuestionsAnswered);

    try {
      await updateQuizProgress(userId, competence.id, newTotalCorrectAnswers, newTotalQuestionsAnswered, currentQuestionIndex);
      if (onQuizComplete) onQuizComplete();

      const isLastQuestion = currentQuestionIndex + 1 >= quizQuestions.length;
      if (isLastQuestion) {
        setQuizCompleted(true);
      } else {
        await saveQuizToFirestore(quizQuestions, currentQuestionIndex + 1);
      }
    } catch (err) {
      console.error("Erreur lors de la mise à jour de la progression :", err);
      setError("Erreur lors de la sauvegarde de la progression.");
    }
  };

  // Fonction pour passer à la question suivante
  const handleNextQuestion = async () => {
    setAnswered(false);
    setSelectedAnswer('');
    setFeedback('');

    if (currentQuestionIndex + 1 < quizQuestions.length) {
      const newIndex = currentQuestionIndex + 1;
      setCurrentQuestionIndex(newIndex);
      try {
        await saveQuizToFirestore(quizQuestions, newIndex);
      } catch (err) {
        console.error("Erreur lors du passage à la question suivante :", err);
        setError("Erreur lors du passage à la question suivante.");
      }
    }
  };

  // Si le quiz est en cours de chargement
  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Chargement du quiz...
        </Typography>
      </Box>
    );
  }

  // Si une erreur est survenue
  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="h6" color="error">{error}</Typography>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={() => setError(null)} 
          sx={{ mt: 2 }}
        >
          Réessayer
        </Button>
      </Box>
    );
  }

  const currentQuestion = quizQuestions[currentQuestionIndex];

  return (
    <MathJaxContext config={mathJaxConfig}>
      <Box>
        <Typography variant="h5" gutterBottom>Quiz</Typography>
        <Typography variant="h6" gutterBottom>
          Bonnes Réponses : {totalCorrectAnswers} / Total Réponses : {totalQuestionsAnswered}
        </Typography>

        {currentQuestion && quizQuestions.length > 0 ? (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body1" component="div" sx={{ fontWeight: 'bold' }}>
                {renderWithMathJax(currentQuestion.question)}
              </Typography>
            </Box>

            <List>
              {currentQuestion.options.map((option, index) => (
                <ListItem key={index}>
                  <Button
                    onClick={() => handleOptionSelect(option)}
                    variant="outlined"
                    fullWidth
                    sx={{
                      backgroundColor: answered
                        ? option === currentQuestion.answer
                          ? '#a5d6a7'
                          : option === selectedAnswer
                          ? '#ef9a9a'
                          : 'transparent'
                        : selectedAnswer === option
                        ? '#cfe8fc'
                        : 'transparent',
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      padding: '8px 16px',
                    }}
                  >
                    <Box component="span">
                      {renderWithMathJax(option)}
                    </Box>
                  </Button>
                </ListItem>
              ))}
            </List>

            {answered && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography component="div">
                  {renderWithMathJax(feedback)}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography variant="h6" color="primary" sx={{ mt: 2, mb: 2 }}>
            {quizQuestions.length === 0 
              ? "Aucun quiz trouvé, veuillez générer un nouveau quiz." 
              : "Vous avez répondu à toutes les questions du quiz !"}
          </Typography>
        )}

        <Box display="flex" justifyContent="flex-start" gap={2} mt={2}>
          {quizQuestions.length === 0 || quizCompleted ? (
            <Button 
              variant="contained" 
              color="secondary" 
              onClick={generateQuiz}
            >
              Générer un nouveau quiz
            </Button>
          ) : (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleAnswerSubmit} 
                disabled={answered || !selectedAnswer}
              >
                Soumettre
              </Button>
              <Button 
                variant="contained" 
                color="secondary" 
                onClick={handleNextQuestion} 
                disabled={!answered}
              >
                Question suivante
              </Button>
            </>
          )}
        </Box>
      </Box>
    </MathJaxContext>
  );
};

export default Quiz;